import debounce from 'lodash/debounce';

import type { TeamMentionResourceConfig } from '@atlaskit/mention/mention-resource';
import { MentionResource } from '@atlaskit/mention/mention-resource';
import { SLI_EVENT_TYPE } from '@atlaskit/mention/analytics';
import {
	AGENT_MENTION_LOAD_ERROR_ID,
	UserAccessLevel,
	UserType,
	type MentionContextIdentifier,
	type MentionPlaceholder,
	type MentionResourceConfig,
	type MentionsResult,
} from '@atlaskit/mention/types';

import { cfetch } from '@confluence/network';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { ExternalUserLozengeProps } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeProps.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { MENTIONS_QUERY_METRIC } from '../perf.config.js';

import { OfflineCache } from './offlineCache.ts';
import { queryAgentMentions } from './queryAgentMentions.ts';

const INITIAL_STATE = 'initialState';
const SEARCH = 'searchUser';
const SUCCEEDED = 'succeeded';
const FAILED = 'failed';
const CONTEXT_TYPE = 'Mentions';

/**
 * MAX_NOTIFIED_ITEMS in platform/packages/elements/mention/src/api/MentionResource.ts limits how many mentions returned by UserTeamMentionProvider will be used really! 20 is its value at the time of this writing.
 */
const MAX_RESULTS = 20;

/**
 * platform/packages/editor/editor-plugin-mentions/src/ui/type-ahead/index.tsx sections the mentions returned by UserTeamMentionProvider in agents and non-agents (such as people and teams).
 * But it also defines its own limit per section! 5 is its limit for agents at the time of this writing.
 * So to see both agents and people/non-agents in the sectioned UI, UserTeamMentionProvider needs to arrange enough of each section into the first MAX_RESULTS mentions it returns.
 */
const MIN_AGENT_RESULTS = 5;

const AGENT_LOADING_PLACEHOLDER_COUNT = 2;
const AGENT_LOADING_PLACEHOLDER_ID_PREFIX = '__confluence-agents-loading-';

const AGENT_MENTIONS_QUERY_TIMEOUT_MS = 5000;

type AgentMentionsQueryResult = {
	result: MentionsResult;
	successful: boolean;
	timedOut: boolean;
	timeoutApplied: boolean;
	queryDurationMs?: number;
};

const ASYNC_AGENT_MENTIONS_REQUEST = Symbol('asyncAgentMentionsRequest');

type AsyncAgentMentionsRequest = {
	initialResultEmitted: Promise<void>;
	sequence: number;
	startedAt: number;
};

type AgentMentionsContextIdentifier = MentionContextIdentifier & {
	[ASYNC_AGENT_MENTIONS_REQUEST]?: AsyncAgentMentionsRequest;
};

const agentMentionsLoadErrorResult = (query: string): MentionsResult => ({
	mentions: [
		{
			appType: 'agent',
			id: AGENT_MENTION_LOAD_ERROR_ID,
			isPlaceholder: true,
			placeholderType: 'error',
			userType: 'APP',
		},
	],
	query,
});

const emptyAgentMentionsResult = (query: string): MentionsResult => ({
	mentions: [],
	query,
});

const createAgentLoadingPlaceholders = (): MentionPlaceholder[] =>
	Array.from({ length: AGENT_LOADING_PLACEHOLDER_COUNT }, (_, index) => ({
		id: `${AGENT_LOADING_PLACEHOLDER_ID_PREFIX}${index}__`,
		isPlaceholder: true,
		placeholderType: 'loading',
		userType: 'APP',
		appType: 'agent',
	}));

const hasPlaceholders = (result: MentionsResult): boolean =>
	result.mentions.some((mention) => mention.isPlaceholder);

const queryAgentMentionsWithoutTimeout = (
	query: string,
	maxResults: number,
	showLoadErrorOnFailure: boolean = false,
): Promise<AgentMentionsQueryResult> =>
	queryAgentMentions(query, maxResults)
		.then(
			(result): AgentMentionsQueryResult => ({
				result,
				successful: true,
				timedOut: false,
				timeoutApplied: false,
			}),
		)
		.catch(
			(): AgentMentionsQueryResult =>
				/* Do not let queryAgentMentions break the production-available people and teams search. */
				({
					result: showLoadErrorOnFailure
						? agentMentionsLoadErrorResult(query)
						: emptyAgentMentionsResult(query),
					successful: false,
					timedOut: false,
					timeoutApplied: false,
				}),
		);

const queryAgentMentionsWithTimeout = async (
	query: string,
	maxResults: number,
	showLoadErrorOnFailure: boolean = true,
): Promise<AgentMentionsQueryResult> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const queryStartedAt = Date.now();

	const timeoutPromise = new Promise<AgentMentionsQueryResult>((resolve) => {
		timeoutId = setTimeout(() => {
			resolve({
				result: showLoadErrorOnFailure
					? agentMentionsLoadErrorResult(query)
					: emptyAgentMentionsResult(query),
				successful: false,
				timedOut: true,
				timeoutApplied: true,
			});
		}, AGENT_MENTIONS_QUERY_TIMEOUT_MS);
	});

	const queryPromise = queryAgentMentionsWithoutTimeout(
		query,
		maxResults,
		showLoadErrorOnFailure,
	).then(
		(result): AgentMentionsQueryResult => ({
			...result,
			queryDurationMs: Date.now() - queryStartedAt,
			timeoutApplied: true,
		}),
	);

	const result = await Promise.race([queryPromise, timeoutPromise]);

	if (timeoutId) {
		clearTimeout(timeoutId);
	}

	return result;
};

// Use in memory cache only
const defaultCache = {
	getStoredValues: () => Promise.resolve(new Map()),
	setStoredValues: () => Promise.resolve(),
};

/**
 * This is an implementation which calls URS to provide a list of recommended users/teams to mention.
 *
 * It is different from `@atlaskit/mention/TeamMentionResource` which combines results from separate calls to
 * pf-mentions for users and legion for teams. Teams are appended after users and therefore will always show up at the
 * bottom of the mention list. Instead, this implem calls URS for a list of users/teams interspersed and ranked.
 * Therefore, if you are likely to mention a team, it will show up in your top results.
 *
 * This extends `MentionResource` to keep the <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/elements/mention/src/api/MentionResource.ts#lines-491">mention selection call to pf-mentions</a>. This will remain until we move to
 * using ui events instead. The missing fields preventing this have been merged and will take a few weeks to release
 * and be added to Confluence. See https://product-fabric.atlassian.net/browse/UR-710. Then, those relying
 * on the pf-mention recorded event will also need to be migrated. Once the blockers are resolved, this implem will be
 * updated to remove the record selection call to pf-mentions.
 *
 * The entry point for a `MentionProvider` is its <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/editor/editor-core/src/plugins/mentions/index.tsx#lines-219">filter method</a>.
 * The `MentionResource` then eventually calls <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/elements/mention/src/api/MentionResource.ts#lines-352">`remoteInitialState`</a> for bootstrap/empty queries,
 * and <a href="https://bitbucket.org/atlassian/atlassian-frontend/src/0884032d85f11f43c13532cd21f13f696b0d28a7/packages/elements/mention/src/api/MentionResource.ts#lines-436">`remoteSearch`</a> for non-empty queries.
 * This implem extends `MentionResource` and overrides those methods to call URS.
 */
export class UserTeamMentionProvider extends MentionResource {
	private userTeamEndpoint: string;
	private teamMentionConfig: TeamMentionResourceConfig;
	private includeExternalCollaborators: boolean;
	private includeAgents: boolean;
	private offlineCache: OfflineCache<MentionsResult> | undefined;
	private isEligibleXProductUserInvite?: () => Promise<boolean>;
	private agentMentionsFilterSequence = 0;

	constructor(
		userTeamEndpoint: string,
		userMentionConfig: MentionResourceConfig,
		teamMentionConfig: TeamMentionResourceConfig,
		includeExternalCollaborators: boolean,
		isEligibleXProductUserInvite?: () => Promise<boolean>,
		includeAgents: boolean = false,
	) {
		// To scale up agent search, we may want to increase the debounce time:
		let superDebounceTime = userMentionConfig.debounceTime ?? 0;
		let initialStateDebounceTime = 0;
		let searchDebounceTime = 0;
		if (includeAgents) {
			initialStateDebounceTime = Math.max(
				expVal('cc-agent-mentions-debounce-time', 'initialState', 0),
				superDebounceTime,
			);
			searchDebounceTime = Math.max(
				expVal('cc-agent-mentions-debounce-time', 'search', 0),
				superDebounceTime,
			);
			if (initialStateDebounceTime > superDebounceTime || searchDebounceTime > superDebounceTime) {
				superDebounceTime = 0; // UserTeamMentionProvider's debounceFilter rather than the super will do the debouncing
			}
		}

		super({
			...userMentionConfig,
			debounceTime: superDebounceTime,
		});

		if (!superDebounceTime && (initialStateDebounceTime || searchDebounceTime)) {
			this.debounceFilter(initialStateDebounceTime, searchDebounceTime);
		}

		this.teamMentionConfig = teamMentionConfig;
		this.userTeamEndpoint = userTeamEndpoint;
		this.includeExternalCollaborators = includeExternalCollaborators;
		this.includeAgents = includeAgents;
		this.isEligibleXProductUserInvite = isEligibleXProductUserInvite;
		const cache = fg('platform_editor_offline_editing_web_ga_fixes') ? defaultCache : undefined;
		this.offlineCache = expValEquals('platform_editor_offline_editing_web', 'isEnabled', true)
			? new OfflineCache<MentionsResult>(cache)
			: undefined;
	}

	// Treatment helpers for platform_editor_mention_search_order are intentionally grouped here.
	async filter(query?: string, contextIdentifier?: MentionContextIdentifier): Promise<void> {
		if (this.includeAgents && isExperimentEnabled('platform_editor_mention_search_order')) {
			return this.filterWithAsyncAgentMentions(query, contextIdentifier);
		}

		// Control: preserve MentionResource's existing single-emission behavior.
		return super.filter(query, contextIdentifier);
	}

	private async filterWithAsyncAgentMentions(
		query?: string,
		contextIdentifier?: MentionContextIdentifier,
	): Promise<void> {
		const sequence = ++this.agentMentionsFilterSequence;
		const startedAt = Date.now();
		let markInitialResultEmitted: () => void = () => {};
		const initialResultEmitted = new Promise<void>((resolve) => {
			markInitialResultEmitted = resolve;
		});
		const asyncContextIdentifier: AgentMentionsContextIdentifier = {
			...contextIdentifier,
			[ASYNC_AGENT_MENTIONS_REQUEST]: {
				initialResultEmitted,
				sequence,
				startedAt,
			},
		};

		try {
			await super.filter(query, asyncContextIdentifier);
		} finally {
			// `super.filter` notifies the first URS result before resolving. Agent
			// enrichment waits on this barrier so it can never become the first emission.
			markInitialResultEmitted();
		}
	}

	private async notifyAgentMentionsWhenReady(
		query: string,
		ursMentions: MentionsResult['mentions'],
		agentMentionsQueryResultPromise: Promise<AgentMentionsQueryResult>,
		request: AsyncAgentMentionsRequest,
	): Promise<void> {
		const [agentMentionsQueryResult] = await Promise.all([
			agentMentionsQueryResultPromise,
			request.initialResultEmitted,
		]);

		if (request.sequence !== this.agentMentionsFilterSequence) {
			return;
		}

		const result = {
			query,
			mentions: this.mergeAsyncAgentMentions(agentMentionsQueryResult.result.mentions, ursMentions),
		};

		this._notifyListeners(result, {
			duration: Date.now() - request.startedAt,
		});
		this._notifyAllResultsListeners(result);

		if (
			agentMentionsQueryResult.successful &&
			query === '' &&
			expValEquals('platform_editor_offline_editing_web', 'isEnabled', true)
		) {
			this.offlineCache?.set(this.userTeamEndpoint, result);
		}
	}

	private mergeAsyncAgentMentions(
		agentMentions: MentionsResult['mentions'],
		ursMentions: MentionsResult['mentions'],
	): MentionsResult['mentions'] {
		if (!agentMentions.length) {
			return ursMentions;
		}

		// Studio agent results should win if URS ever returns the same agent ID.
		const agentMentionIds = new Set(agentMentions.map((mention) => mention.id));
		const distinctUrsMentions = ursMentions.filter((mention) => !agentMentionIds.has(mention.id));
		const limitedAgentMentions = agentMentions.slice(
			0,
			Math.max(MIN_AGENT_RESULTS, MAX_RESULTS - distinctUrsMentions.length),
		);

		return [...limitedAgentMentions, ...distinctUrsMentions];
	}

	private debounceFilter(initialStateDebounceTime: number, searchDebounceTime: number) {
		const filter = this.filter.bind(this);
		const debouncedInitialStateFilter = debounce(filter, initialStateDebounceTime);
		const debouncedSearchFilter = debounce(filter, searchDebounceTime);

		this.filter = async (query, contextIdentifier) => {
			if (query) {
				debouncedInitialStateFilter.cancel();
				return debouncedSearchFilter(query, contextIdentifier);
			} else {
				debouncedSearchFilter.cancel();
				return debouncedInitialStateFilter(query, contextIdentifier);
			}
		};
	}

	/**
	 * Overrides `remoteInitialState` from `MentionResource`
	 * This is invoked for bootstrap/empty queries,
	 */
	async remoteInitialState(contextIdentifier?: MentionContextIdentifier): Promise<MentionsResult> {
		try {
			const promise = await this.doSearch('', contextIdentifier);
			// Control: cache the single settled result here. Treatment caches only from
			// notifyAgentMentionsWhenReady after both sources have succeeded.
			if (
				(!this.includeAgents || !isExperimentEnabled('platform_editor_mention_search_order')) &&
				expValEquals('platform_editor_offline_editing_web', 'isEnabled', true)
			) {
				this.offlineCache?.set(this.userTeamEndpoint, promise);
			}
			this._notifyAnalyticsListeners(SLI_EVENT_TYPE, INITIAL_STATE, SUCCEEDED);
			return promise;
		} catch (e) {
			this._notifyAnalyticsListeners(SLI_EVENT_TYPE, INITIAL_STATE, FAILED);
			throw e;
		}
	}

	/**
	 * Overrides `remoteSearch` from `MentionResource`
	 * This is invoked for non-empty queries.
	 */
	async remoteSearch(
		query: any,
		contextIdentifier?: MentionContextIdentifier,
	): Promise<MentionsResult> {
		try {
			const promise = await this.doSearch(query, contextIdentifier);
			this._notifyAnalyticsListeners(SLI_EVENT_TYPE, SEARCH, SUCCEEDED);
			return promise;
		} catch (e) {
			this._notifyAnalyticsListeners(SLI_EVENT_TYPE, SEARCH, FAILED);
			throw e;
		}
	}

	private async getCachedValue(query: string): Promise<MentionsResult | undefined> {
		const cachedValue = this.offlineCache?.get(this.userTeamEndpoint);
		if (!cachedValue) {
			return undefined;
		}
		if (query === '') {
			return Promise.resolve(cachedValue);
		} else {
			// If we're offline allow basic filtering of the cached results by name.
			if (window?.navigator.onLine === false) {
				return Promise.resolve({
					query,
					mentions: cachedValue.mentions?.filter(
						(v) =>
							v?.name?.toLowerCase().includes(query.toLowerCase()) ||
							v?.mentionName?.toLowerCase().includes(query.toLowerCase()) ||
							v?.nickname?.toLowerCase().includes(query.toLowerCase()),
					),
				});
			}
		}
	}

	private async doSearch(query, contextIdentifier?): Promise<MentionsResult> {
		if (expValEquals('platform_editor_offline_editing_web', 'isEnabled', true)) {
			const cachedValue = await this.getCachedValue(query);
			// Use stale-while-revalidate - if the cached value exists use it first and
			// update the cache value in the background so that it's up to date when the
			// next call is made
			if (cachedValue) {
				if (query === '') {
					this.doNetworkSearch(query, contextIdentifier)
						.then((results) => {
							if (
								!hasPlaceholders(results) ||
								!isExperimentEnabled('platform_editor_mention_search_order')
							) {
								this.offlineCache?.set(this.userTeamEndpoint, results);
							}
						})
						.catch(() => {
							// Pass, it's okay if the cache doesn't update
						});
				}
				return cachedValue;
			}

			return this.doNetworkSearch(query, contextIdentifier);
		} else {
			return this.doNetworkSearch(query, contextIdentifier);
		}
	}

	private async doNetworkSearch(query, contextIdentifier?): Promise<MentionsResult> {
		MENTIONS_QUERY_METRIC.start();
		const asyncAgentMentionsRequest = (
			contextIdentifier as AgentMentionsContextIdentifier | undefined
		)?.[ASYNC_AGENT_MENTIONS_REQUEST];
		const responsePromise = (async () =>
			this.post(await this.cloudId(), await this.orgId(), query, contextIdentifier))();
		const agentMentionsQueryResultPromise = this.includeAgents
			? asyncAgentMentionsRequest && isExperimentEnabled('platform_editor_mention_search_order')
				? queryAgentMentionsWithTimeout(
						query,
						MAX_RESULTS,
						expValEquals('platform_editor_agent_mentions_rovo_query_timeout', 'isEnabled', true),
					)
				: expValEquals('platform_editor_agent_mentions_rovo_query_timeout', 'isEnabled', true)
					? queryAgentMentionsWithTimeout(query, MAX_RESULTS)
					: queryAgentMentionsWithoutTimeout(query, MAX_RESULTS)
			: undefined;

		const response = await responsePromise;

		if (!response.ok) {
			const errorBody = await response.json();

			MENTIONS_QUERY_METRIC.stop({
				customData: {
					isExCoEnabled: this.includeExternalCollaborators,
					success: false,
				},
			});

			throw new Error(JSON.stringify(errorBody));
		}

		const body = await response.json();

		const mentions = body.recommendedUsers.map((recommended) => {
			if (this.isAgent(recommended)) return this.convertToAgentMention(recommended);
			if (this.isUser(recommended)) return this.convertToUserMention(recommended);
			if (this.isTeam(recommended)) return this.convertToTeamMention(recommended);
			// Fallback to user mention for unknown types
			return this.convertToUserMention(recommended);
		});

		let jiraMentions = [];

		if (
			mentions.length < MAX_RESULTS &&
			this.isEligibleXProductUserInvite &&
			(await this.isEligibleXProductUserInvite())
		) {
			try {
				const jiraResponse = await this.searchXproductUsers(
					await this.cloudId(),
					await this.orgId(),
					query,
					contextIdentifier,
				);
				if (!jiraResponse.ok) {
					const errorBody = await jiraResponse.json();
					throw new Error(JSON.stringify(errorBody));
				}
				const jiraBody = await jiraResponse.json();

				// Remove objects from jiraMentions if an object with the same id is present in mentions
				const mentionIds = new Set(mentions.map((mention) => mention.id));

				const recommendedJiraUsers = (jiraBody.recommendedUsers || [])
					.filter((mention) => !mentionIds.has(mention?.id))
					.slice(0, MAX_RESULTS - mentions.length);

				//only evaluate the experiment cohort if there are jira mentions to show
				if (
					recommendedJiraUsers.length > 0 &&
					expValEquals('cc_virality_x_product_user_invite', 'cohort', 'test')
				) {
					jiraMentions = recommendedJiraUsers.map((recommended) => {
						return { ...this.convertToUserMention(recommended), isXProductUser: true };
					});
				}
			} catch (error) {
				const client = await getAnalyticsWebClient();
				const errorMessages = error instanceof Error ? error.message : String(error);
				client.sendOperationalEvent({
					source: 'editor',
					action: 'errored',
					actionSubject: 'xProductUserSearch',
					attributes: {
						error: errorMessages,
						stack: error?.stack,
					},
				});
			}
		}

		// Treatment: return URS immediately and publish Agent Studio in a later emission.
		if (
			isExperimentEnabled('platform_editor_mention_search_order') &&
			agentMentionsQueryResultPromise &&
			asyncAgentMentionsRequest
		) {
			MENTIONS_QUERY_METRIC.stop({
				customData: {
					isExCoEnabled: this.includeExternalCollaborators,
					success: true,
				},
			});

			void this.notifyAgentMentionsWhenReady(
				query,
				[...mentions, ...jiraMentions],
				agentMentionsQueryResultPromise,
				asyncAgentMentionsRequest,
			);

			return {
				query,
				// AbstractMentionResource only notifies its first 20 rows. Put placeholders
				// first so a full URS response cannot truncate the loading state.
				mentions: [...createAgentLoadingPlaceholders(), ...mentions, ...jiraMentions],
			};
		}

		// Control: preserve the existing single-emission critical path below.
		const agentMentionsQueryResult = await agentMentionsQueryResultPromise;

		MENTIONS_QUERY_METRIC.stop({
			customData: {
				isExCoEnabled: this.includeExternalCollaborators,
				success: true,
				...(agentMentionsQueryResult?.timeoutApplied
					? {
							...(agentMentionsQueryResult.queryDurationMs !== undefined
								? {
										agentMentionsQueryDurationMs: agentMentionsQueryResult.queryDurationMs,
									}
								: {}),
							agentMentionsTimedOut: agentMentionsQueryResult?.timedOut ?? false,
							agentMentionsTimeoutMs: AGENT_MENTIONS_QUERY_TIMEOUT_MS,
						}
					: {}),
			},
		});

		let agentMentions = agentMentionsQueryResult?.result.mentions ?? [];
		let nonAgentMentions = [...mentions, ...jiraMentions];

		if (agentMentions.length) {
			// Studio agent results should win if URS ever returns the same agent ID.
			const agentMentionIds = new Set(agentMentions.map((mention) => mention.id));
			nonAgentMentions = nonAgentMentions.filter((mention) => !agentMentionIds.has(mention.id));

			agentMentions = agentMentions.slice(
				0,
				Math.max(MIN_AGENT_RESULTS, MAX_RESULTS - nonAgentMentions.length),
			);
		}

		return {
			query,
			mentions: [...agentMentions, ...nonAgentMentions],
		};
	}

	private async cloudId() {
		const { cloudId } = await getSessionData();
		return cloudId;
	}

	private async orgId() {
		const { orgId } = await getSessionData();
		return orgId;
	}

	private async post(cloudId, orgId, query, contextIdentifier?) {
		return await cfetch(this.userTeamEndpoint, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify({
				context: {
					contextType: CONTEXT_TYPE,
					principalId: 'context',
					productKey: (contextIdentifier && contextIdentifier.product) || 'confluence', // defaults to 'confluence' to work with old editor
					siteId: cloudId,
					containerId: contextIdentifier && contextIdentifier.containerId,
					objectId: contextIdentifier && contextIdentifier.objectId,
					childObjectId: contextIdentifier && contextIdentifier.childObjectId,
					mentionsSessionId: contextIdentifier && contextIdentifier.sessionId,
					...(orgId ? { organizationId: orgId } : {}),
					...(this.includeExternalCollaborators
						? {
								productAttributes: {
									isEntitledConfluenceExternalCollaborator: true,
								},
							}
						: {}),
				},
				searchQuery: {
					queryString: query,
					...(this.includeExternalCollaborators
						? {
								productAccessPermissionIds: ['write', 'external-collaborator-write'],
							}
						: {}),
				},
				maxNumberOfResults: MAX_RESULTS,
				includeTeams: true,
			}),
		});
	}

	private async searchXproductUsers(cloudId, orgId, query, contextIdentifier?) {
		return await cfetch(this.userTeamEndpoint, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			credentials: 'include',
			body: JSON.stringify({
				context: {
					contextType: CONTEXT_TYPE,
					principalId: 'context',
					productKey: 'jira', // fetch only jira users
					siteId: cloudId,
					mentionsSessionId: contextIdentifier && contextIdentifier.sessionId,
					...(orgId ? { organizationId: orgId } : {}),
				},
				searchQuery: {
					queryString: query,
					productAccessPermissionIds: ['write'],
				},
				maxNumberOfResults: MAX_RESULTS,
				includeTeams: false, // Do not include teams
			}),
		});
	}

	private isAgent(recommended) {
		// Agents are returned as USER entity type with appType 'agent'
		return recommended.entityType === 'USER' && recommended.appType === 'agent';
	}

	private convertToAgentMention(recommended) {
		return {
			id: recommended.id,
			name: recommended.name || recommended.nickname || '',
			mentionName: recommended.nickname || '',
			avatarUrl: recommended.avatarUrl,
			accessLevel: recommended.accessLevel,
			userType: 'APP', // Mark as app/agent type
			appType: 'agent',
		};
	}

	private isUser(recommended) {
		// Regular users have entityType USER but no agent appType
		return recommended.entityType === 'USER' && recommended.appType !== 'agent';
	}

	private convertToUserMention(recommended) {
		return {
			id: recommended.id,
			name: recommended.name || recommended.nickname || '',
			mentionName: recommended.nickname || '',
			avatarUrl: recommended.avatarUrl,
			accessLevel: recommended.accessLevel,
			userType: recommended.userType,
			...(recommended?.attributes?.isConfluenceExternalCollaborator
				? {
						lozenge: ExternalUserLozengeProps,
					}
				: {}),
		};
	}

	private isTeam(recommended) {
		return recommended.entityType === 'TEAM';
	}

	private convertToTeamMention(recommended) {
		const { teamLinkResolver } = this.teamMentionConfig;
		let teamLink = '';
		const defaultTeamLink = `${window.location.origin}/people/team/${recommended.id}`;
		if (typeof teamLinkResolver === 'function') {
			teamLink = teamLinkResolver(recommended.id);
		}
		return {
			id: recommended.id,
			name: recommended.displayName,
			mentionName: recommended.displayName,
			avatarUrl: recommended.smallAvatarImageUrl,
			accessLevel: UserAccessLevel[UserAccessLevel.CONTAINER],
			userType: UserType[UserType.TEAM],
			context: {
				members: recommended.members,
				includesYou: recommended.includesYou,
				memberCount: recommended.memberCount,
				teamLink: teamLink || defaultTeamLink,
			},
		};
	}
}
